// frontend/source/portal/pages/AdminArtistArtworkDetailsPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import AdminArtworkDisplay from "../components/AdminArtworkDisplay";
import AdminArtworkDetails from "../components/AdminArtworkDetails";
import NavigationButtons from "../../components/ArtworkPage/NavigationButtons";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";
import "./AdminArtworkDetailsPage.css";

const AdminArtistArtworkDetailsPage = () => {
  const { artworkId, artistId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [artworkList, setArtworkList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [artistName, setArtistName] = useState("");

  useEffect(() => {
    const loadArtwork = async () => {
      try {
        setLoading(true);
        console.log("AdminArtistArtworkDetailsPage", artistId);

        // Load artist details
        const artistData = await api.getArtistById(artistId);
        setArtistName(
          artistData.fullName || `${artistData.fname} ${artistData.lname}`
        );

        if (location.state?.artworkList) {
          const list = location.state.artworkList;
          const index = list.findIndex((a) => a._id === artworkId);
          setArtworkList(list);
          setCurrentIndex(index);
          setCurrentArtwork(list[index]);
        } else {
          // Load all artworks for this artist
          const artworksData = await api.getArtworksByArtist(artistId, 1, 0, false);
          const list = artworksData.artworks || artworksData;
          const index = list.findIndex((a) => a._id === artworkId);
          setArtworkList(list);
          setCurrentIndex(index);
          setCurrentArtwork(list[index]);
        }
      } catch (err) {
        console.error("Error loading artwork:", err);
      } finally {
        setLoading(false);
      }
    };

    loadArtwork();
  }, [artworkId, artistId, location.state]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newArtwork = artworkList[newIndex];
      navigate(`/portal/student/${artistId}/artwork/${newArtwork._id}`, {
        state: { artworkList, artistName },
        replace: true,
      });
    }
  };

  const goToNext = () => {
    if (currentIndex < artworkList.length - 1) {
      const newIndex = currentIndex + 1;
      const newArtwork = artworkList[newIndex];
      navigate(`/portal/student/${artistId}/artwork/${newArtwork._id}`, {
        state: { artworkList, artistName },
        replace: true,
      });
    }
  };

  const handleBackToArtworks = () => {
    navigate(`/portal/student/${artistId}/artworks`);
  };

  const handleUpdateArtwork = async (updates) => {
    try {
      const updated = await adminApi.updateArtwork(currentArtwork._id, updates);
      setCurrentArtwork(updated);
      // Update in the list as well
      setArtworkList(
        artworkList.map((art) => (art._id === updated._id ? updated : art))
      );
    } catch (err) {
      console.error("Failed to update artwork:", err);
      throw err;
    }
  };

  useKeyboardNavigation(
    goToPrevious,
    goToNext,
    currentIndex,
    artworkList.length
  );

  if (loading || !currentArtwork) {
    return <LoadingSpinner message="Loading artwork..." />;
  }

  return (
    <div>
      <NavigationButtons
        onPrevious={goToPrevious}
        onNext={goToNext}
        hasPrevious={currentIndex > 0}
        hasNext={currentIndex < artworkList.length - 1}
      />

      <div className="admin-artwork-main">
        <div className="admin-artwork-content">
          {/* Breadcrumb Navigation */}
          <nav className="admin-artwork-breadcrumb">
            <button
              className="admin-artwork-breadcrumb-link"
              onClick={handleBackToArtworks}
              aria-label={`Back to ${artistName} artworks`}
            >
              <svg
                className="admin-artwork-breadcrumb-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {artistName}
            </button>
            <span className="admin-artwork-breadcrumb-separator">/</span>
            <span className="admin-artwork-breadcrumb-current">
              {currentArtwork.title}
            </span>
          </nav>

          <AdminArtworkDisplay
            artwork={currentArtwork}
            onUpdate={handleUpdateArtwork}
          />
          <AdminArtworkDetails
            artwork={currentArtwork}
            currentIndex={currentIndex}
            totalCount={artworkList.length}
            onUpdate={handleUpdateArtwork}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminArtistArtworkDetailsPage;
